import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    FormControl,
    FormLabel,
    Input,
    Text,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { buildBulkDeleteConfirmPhrase } from '../../hooks/inventory/useDeleteInventoryItem';

interface BulkDeleteInventoryConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    itemCount: number;
    onConfirm: (confirmPhrase: string) => void;
    isLoading?: boolean;
}

const BulkDeleteInventoryConfirmDialog = ({
    isOpen,
    onClose,
    itemCount,
    onConfirm,
    isLoading = false,
}: BulkDeleteInventoryConfirmDialogProps) => {
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [confirmText, setConfirmText] = useState('');
    const expectedPhrase = buildBulkDeleteConfirmPhrase(itemCount);

    useEffect(() => {
        if (!isOpen) setConfirmText('');
    }, [isOpen]);

    const canDelete = confirmText === expectedPhrase && !isLoading && itemCount > 0;

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
            isCentered
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="md">
                        Delete {itemCount} item{itemCount === 1 ? '' : 's'}
                    </AlertDialogHeader>
                    <AlertDialogBody fontSize="sm">
                        <Text mb={3}>
                            You are about to permanently delete{' '}
                            <Text as="span" fontWeight="semibold">
                                {itemCount}
                            </Text>{' '}
                            inventory item{itemCount === 1 ? '' : 's'}. This cannot be undone.
                        </Text>
                        <FormControl>
                            <FormLabel fontSize="sm">
                                Type{' '}
                                <Text as="span" fontFamily="mono" fontWeight="semibold">
                                    {expectedPhrase}
                                </Text>{' '}
                                to confirm
                            </FormLabel>
                            <Input
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter') return;
                                    e.preventDefault();
                                    if (!canDelete) return;
                                    onConfirm(expectedPhrase);
                                }}
                                placeholder={expectedPhrase}
                                autoFocus
                                fontFamily="mono"
                                fontSize="sm"
                            />
                        </FormControl>
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button size="action" variant="outlineGray" ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
                            Cancel
                        </Button>
                        <Button
                            size="action"
                            colorScheme="red"
                            ml={3}
                            isDisabled={!canDelete}
                            isLoading={isLoading}
                            onClick={() => onConfirm(expectedPhrase)}
                        >
                            Delete
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

export default BulkDeleteInventoryConfirmDialog;
